import React from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import SiteFooter from "@/components/SiteFooter";

const LAST_UPDATED = "18 June 2026";

export default function Privacy() {
 return (
 <div className="min-h-screen bg-[#FAF7F0]">
 <Navbar />
 <main className="max-w-3xl mx-auto px-6 sm:px-8 lg:px-12 py-12 sm:py-16" data-testid="privacy-page">
 <div className="mb-10">
 <div className="label-mono text-[#1B6C62] mb-3">Legal</div>
 <h1 className="font-serif text-4xl sm:text-5xl text-[#0D2B52] mb-3">Privacy Policy</h1>
 <p className="text-sm text-[#6B7280] font-mono">Last updated: {LAST_UPDATED}</p>
 </div>

 <article className="space-y-8 text-[15px] text-[#1A1A1A] leading-relaxed font-serif">
 <section>
 <h2 className="font-serif text-2xl text-[#0D2B52] mb-3">Who we are</h2>
 <p>TenantTrail is an Australian-based service that helps tenants organise tenancy documents, capture evidence, and prepare for tribunal disputes. This Privacy Policy explains how we collect, use, store, and protect your personal information when you use our website and services. We comply with the Australian Privacy Principles (APPs) under the Privacy Act 1988 (Cth).</p>
 </section>

 <section>
 <h2 className="font-serif text-2xl text-[#0D2B52] mb-3">What information we collect</h2>
 <p>We only collect what we genuinely need to provide the service:</p>
 <ul className="list-disc list-outside pl-5 mt-3 space-y-2">
 <li><strong>Account information</strong>. your email, hashed password, and display name.</li>
 <li><strong>Case content you upload</strong>. documents, photographs, voice notes, inspection reports, contacts, and any text you write into cases.</li>
 <li><strong>Embedded metadata</strong>. EXIF data preserved on uploaded photographs (capture date, device, GPS where present). This is the same metadata your camera already stored in the file; we preserve it rather than strip it, because it is forensically valuable.</li>
 <li><strong>Email audit data</strong>. when you send a letter through TenantTrail we record the recipient, send time, content hash, and the delivery / open / bounce events Resend reports back to us.</li>
 <li><strong>Payment information</strong>. we use Stripe for payments. We never see or store your full card number; Stripe handles that under their PCI-DSS Level 1 certification. We retain your subscription tier, transaction IDs, and amounts.</li>
 <li><strong>Lead capture</strong>. when you use our public &ldquo;Check a rental&rdquo; tool we record the address you searched and (if you supply one) your email. We use this to follow up about your rental and to improve the tool.</li>
 <li><strong>Standard server logs</strong>. IP, browser user-agent, and timestamps of requests, retained for 30 days for security.</li>
 </ul>
 </section>

 <section>
 <h2 className="font-serif text-2xl text-[#0D2B52] mb-3">How we use your information</h2>
 <ul className="list-disc list-outside pl-5 space-y-2">
 <li>To deliver the core service: storing your evidence, drafting letters, generating AI guidance, and preparing tribunal-ready bundles.</li>
 <li>To process payments and manage your subscription via Stripe.</li>
 <li>To send transactional emails (receipts, password resets, security alerts) via Resend.</li>
 <li>To improve the AI: anonymised, aggregated patterns may be used to refine prompts and detect issues. We do <strong>not</strong> train third-party AI models on your case content.</li>
 <li>To comply with our legal obligations (for example, responding to a lawful subpoena).</li>
 </ul>
 </section>

 <section>
 <h2 className="font-serif text-2xl text-[#0D2B52] mb-3">Third parties we use</h2>
 <p>To run the service we share necessary data with these vetted processors:</p>
 <ul className="list-disc list-outside pl-5 mt-3 space-y-2">
 <li><strong>Anthropic (Claude)</strong>. your AI questions and the relevant case context are sent to Anthropic to generate the response. Anthropic does not train on this data per their commercial terms.</li>
 <li><strong>OpenAI</strong>. used only for voice transcription (Whisper) when you record voice notes. Voice data is sent for transcription and not retained by OpenAI under their API terms.</li>
 <li><strong>Stripe</strong>. payment processing.</li>
 <li><strong>Resend</strong>. outbound email delivery and inbound email reception.</li>
 <li><strong>MongoDB Atlas</strong>. encrypted database storage.</li>
 <li><strong>Cloud hosting</strong>. our application runs on cloud infrastructure with industry-standard security controls.</li>
 </ul>
 <p className="mt-3">We do not sell your personal information. We do not share it with advertisers.</p>
 </section>

 <section>
 <h2 className="font-serif text-2xl text-[#0D2B52] mb-3">Where your data is stored</h2>
 <p>Your data is stored on cloud infrastructure that may be located inside or outside Australia. When data is transferred internationally we ensure the receiving party is bound by privacy obligations consistent with Australian Privacy Principle 8.</p>
 </section>

 <section>
 <h2 className="font-serif text-2xl text-[#0D2B52] mb-3">How long we keep your data</h2>
 <p>We retain your case data for as long as your account is active, plus a sensible window after deletion in case you need to restore it. You can request immediate deletion at any time by emailing the address below. We will respond to deletion requests within 30 days.</p>
 <p className="mt-3">We recommend you export your tribunal evidence bundle PDF when you no longer need an active account, so your evidence remains useful to you even after deletion.</p>
 </section>

 <section>
 <h2 className="font-serif text-2xl text-[#0D2B52] mb-3">Security</h2>
 <p>We use TLS encryption in transit, encrypted database storage at rest, and hashed passwords. Sensitive integrations (Stripe, Resend, Anthropic) are accessed via API keys held only in our server-side environment, never in the browser.</p>
 <p className="mt-3">No system is perfect. If a breach occurred we would notify you and the Office of the Australian Information Commissioner in accordance with the Notifiable Data Breaches scheme.</p>
 </section>

 <section>
 <h2 className="font-serif text-2xl text-[#0D2B52] mb-3">Your rights</h2>
 <p>Under the Australian Privacy Principles you have the right to:</p>
 <ul className="list-disc list-outside pl-5 mt-3 space-y-2">
 <li>Access the personal information we hold about you.</li>
 <li>Correct information that is inaccurate, incomplete, or out of date.</li>
 <li>Request deletion of your account and data.</li>
 <li>Withdraw consent for non-essential processing.</li>
 <li>Make a complaint to the Office of the Australian Information Commissioner (oaic.gov.au) if you believe we have mishandled your data.</li>
 </ul>
 </section>

 <section>
 <h2 className="font-serif text-2xl text-[#0D2B52] mb-3">Cookies</h2>
 <p>We use a small number of strictly necessary cookies for authentication (your login session) and security (CSRF protection). We do not use third-party advertising or tracking cookies.</p>
 </section>

 <section>
 <h2 className="font-serif text-2xl text-[#0D2B52] mb-3">Children</h2>
 <p>TenantTrail is intended for adults entering into tenancy agreements. We do not knowingly collect data from people under 18.</p>
 </section>

 <section>
 <h2 className="font-serif text-2xl text-[#0D2B52] mb-3">Contact us</h2>
 <p>Questions, access requests, or deletion requests can be sent to <a href="mailto:support@tenanttrail.net" className="text-[#1B6C62] underline">support@tenanttrail.net</a>. We aim to respond within five business days.</p>
 </section>

 <section>
 <h2 className="font-serif text-2xl text-[#0D2B52] mb-3">Changes to this policy</h2>
 <p>If we make material changes we will email registered users at least 14 days before they take effect. Continued use after that date constitutes acceptance.</p>
 </section>
 </article>

 <div className="mt-12 pt-8 border-t border-[#E5E4DE] text-sm text-[#6B7280] font-serif italic">
 <p>This policy is provided in plain English to be understandable. Where it conflicts with the Privacy Act 1988 (Cth), the Act prevails.</p>
 <p className="mt-3">
 See also: <Link to="/terms" className="text-[#0D2B52] underline">Terms of Service</Link>
 </p>
 </div>
 
      <SiteFooter />
      </main>
 </div>
 );
}
